import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import '../plans/plans.scss';
import CheckCircleIcon from '../../assets/icon-components/CheckCircle';
import arrowRightIcon from '../../assets/icons/whiteArrow.png';
import useMediaQuery from '@mui/material/useMediaQuery';
import bannerImg2 from '../../assets/images/BannerCoreVitalsPlusMembership.png';
import checkIcon from '../../assets/icons/CheckTick.svg';
import { getStripeProducts, StripeProduct } from '../../api/subscription';
import { API_V1_PREFIX, isProd } from '../../utils/constants';
import { firePlanChange, firePaymentTypeChange } from '../../utils/dataLayer';

const PlanHeader = ({
  title,
  description,
  price,
  id,
  selectedPlan,
  onSelect
}: {
  title: string;
  description: string;
  price: React.ReactNode;
  id: string;
  selectedPlan: string;
  onSelect: (id: string) => void;
}) => {
  return (
    <div className='plan-card-header d-flex' >
      <div>
        <input
          type='radio'
          name='plan-type'
          className='plan-type-radio'
          checked={selectedPlan === id}
          onChange={() => onSelect(id)}
        />
        <span className='radio-btn'></span>
      </div>
      <div className='plan-card-header-info' >
        <div className='' >
          {title}
          <p>{description}</p>
        </div>
        <div className='plan-card-header-price' >{price}</div>
      </div>
    </div>
  );
};

const BloodTestPerYear = ({
  selectedTestCount,
  onTestCountChange,
  availableTestCounts
}: {
  selectedTestCount: number | null;
  onTestCountChange: (count: number) => void;
  availableTestCounts: number[];
}) => {
  return (
    <div className='blood-test-per-year'>
      <div className='text-center'>
        <h4>Select the amount of blood tests you want per year</h4>
        <p>
          <CheckCircleIcon />
          Includes free nurse blood draw at home
        </p>
      </div>
      <div className='blood-test-per-year-buttons'>
        {availableTestCounts.map((count) => (
          <button
            key={count}
            onClick={(e) => {
              e.stopPropagation();
              onTestCountChange(count);
            }}
            className={`btn ${selectedTestCount === count ? 'active' : ''}`}
          >
            {count} test{count > 1 ? 's' : ''}
          </button>
        ))}
      </div>
    </div>
  );
};

const plusPlan = {
  id: 'coreVitalsPlus',
  className: 'coreVitals-plus-membership',
  title: 'CoreVitals+ Membership',
  description: 'CoreVitals+ Membership – Deeper insight across bloods combined with MRI imaging.',
  price: <>~ £175<span>/month</span></>,
  bannerImg: bannerImg2,
  list: [
    '1 blood biomarker test per year',
    'Includes free nurse blood draw at home',
    'Full biomarker panel',
    'Doctor review + consult',
    'Nutritionist guidance',
    'App access to track progress',
    'Annual full-body MRI',
    'Pre- and post-scan clinician consults',
    'Combined blood and MRI insights in-app',
    'Ongoing tracking and progress monitoring'
  ] as string[]
};

type PlanType = 'coreVitalsPlus' | '';
type PaymentType = 'monthly' | 'yearly';

interface OrderSummaryProps {
  selectedPlanId: string;
  paymentType: PaymentType;
  selectedTestCount: number | null;
  getPlanPrice: (planId: PlanType) => React.ReactNode;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  selectedPlanId,
  paymentType,
  selectedTestCount,
  getPlanPrice
}) => {
  const isMobile = useMediaQuery('(max-width: 767px)');

  let itemsToShow = plusPlan.list;

  let displayTitle = plusPlan.title;
  if (selectedTestCount) {
    displayTitle = `${plusPlan.title} ${selectedTestCount} test${
      selectedTestCount > 1 ? 's' : ''
    }`;
  }

  if (selectedTestCount) {
    itemsToShow = itemsToShow.map((item) => {
      if (item.includes('blood biomarker test per year')) {
        return `${selectedTestCount} blood biomarker test${selectedTestCount > 1 ? 's' : ''} per year`;
      }
      return item;
    });
  }

  return (
    <div className='plan-info'>
      <div className='order-summary-banner'>
        <img src={plusPlan.bannerImg} alt={`${displayTitle} banner`} />
      </div>
      {!isMobile && <div className='order-summary-title'>{displayTitle}</div>}
      {!isMobile && (
        <p className='order-summary-description'>{plusPlan.description}</p>
      )}
      <ul className='order-summary-list'>
        {itemsToShow.map((item, index) => (
          <li key={`${item}-${index}`}>
            <img src={checkIcon} alt='check' />
            {item}
          </li>
        ))}
      </ul>
      <div className='total-price'>
        <div>Total</div>
        <div>{getPlanPrice(selectedPlanId as PlanType)}</div>
      </div>
    </div>
  );
};

interface SelectedPlanData {
  type: PlanType;
  testCount: number | null;
  product: StripeProduct | null;
  productId: string | null;
  paymentType: PaymentType;
}

const PlansPlus: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedPlan, setSelectedPlan] = useState<PlanType>('coreVitalsPlus');
  const [selectedTestCount, setSelectedTestCount] = useState<number | null>(
    null
  );
  const [paymentType, setPaymentType] = useState<PaymentType>('monthly');
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useMediaQuery('(max-width: 767px)');

  const getCoreVitalsPlusProducts = () => {
    return products
      .filter((p) => p.key.startsWith('core_plus_'))
      .sort((a, b) => {
        const aNum = parseInt(a.key.match(/\d+/)?.[0] || '0');
        const bNum = parseInt(b.key.match(/\d+/)?.[0] || '0');
        return aNum - bNum;
      });
  };

  const getAvailableTestCounts = (): number[] => {
    return getCoreVitalsPlusProducts().map((p) =>
      parseInt(p.key.match(/\d+/)?.[0] || '0')
    );
  };

  useEffect(() => {
    const planTypeParam =
      searchParams.get('plan_type') || searchParams.get('planType');
    if (planTypeParam === 'coreVitalsPlus' || planTypeParam === '') {
      setSelectedPlan(planTypeParam as PlanType);
    }
  }, [searchParams]);

  useEffect(() => {
    const paymentTypeParam =
      searchParams.get('payment_type') || searchParams.get('paymentType');
    if (paymentTypeParam === 'yearly' || paymentTypeParam === 'monthly') {
      setPaymentType(paymentTypeParam);
    }
  }, [searchParams]);

  useEffect(() => {
    const testCountParam =
      searchParams.get('testCount') || searchParams.get('test_count');
    if (testCountParam) {
      const count = parseInt(testCountParam, 10);
      if (!isNaN(count) && count > 0) {
        setSelectedTestCount(count);
      }
    }
  }, [searchParams]);

  // Fetch member details if token is present (after products are loaded)
  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam || products.length === 0) return;

    const fetchMemberDetails = async () => {
      try {
        const baseURL = isProd
          ? process.env.REACT_APP_API_BASE_URL_PROD
          : process.env.REACT_APP_API_BASE_URL_DEV;
        const response = await axios.get(
          `${baseURL}/${API_V1_PREFIX}members?loading_strategy=with_subscription`,
          {
            headers: {
              Authorization: `Bearer ${tokenParam}`,
            },
          }
        );

        const member = response.data;

        // Set test count from member data
        if (member.number_of_blood_test_plan) {
          const testCount = member.number_of_blood_test_plan;
          // Verify the test count is available in the products
          const availableCounts = getAvailableTestCounts();
          if (availableCounts.includes(testCount)) {
            setSelectedTestCount(testCount);
          }
        }

        // Set billing cycle from member data
        if (member.billing_cycle) {
          const billingCycle = member.billing_cycle.toLowerCase();
          if (billingCycle === 'yearly' || billingCycle === 'monthly') {
            setPaymentType(billingCycle as PaymentType);
          }
        }
      } catch (error) {
        console.error('Error fetching member details:', error);
      }
    };

    fetchMemberDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, products]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await getStripeProducts();
        const activeProducts = response.products.filter(
          (product: StripeProduct) => product.active
        );
        setProducts(activeProducts);
      } catch (err: any) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    if (!selectedTestCount && products.length > 0) {
      const availableCounts = getAvailableTestCounts();
      if (availableCounts.length > 0) {
        setSelectedTestCount(availableCounts[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, selectedTestCount]);

  const getSelectedProduct = (): StripeProduct | null => {
    if (!selectedTestCount) return null;
    const productKey = `core_plus_${selectedTestCount}_test`;
    return products.find((p) => p.key === productKey) || null;
  };

  const getPlanPrice = (planId: PlanType): React.ReactNode => {
    let productToUse: StripeProduct | null = null;

    if (selectedTestCount) {
      productToUse = getSelectedProduct();
    }

    if (!productToUse) {
      const availableProducts = getCoreVitalsPlusProducts();
      productToUse = availableProducts[0] || null;
    }

    if (productToUse) {
      const amount =
        paymentType === 'monthly'
          ? productToUse.monthly_amount
          : productToUse.yearly_amount;

      if (amount !== null && amount !== undefined) {
        const showTilde = !selectedTestCount;
        return (
          <>
            {showTilde ? '~ ' : ''}£{amount.toLocaleString()}
            <span>/{paymentType === 'monthly' ? 'month' : 'year'}</span>
          </>
        );
      }
    }

    return (
      <>
        ~ £175<span>/month</span>
      </>
    );
  };

  const handlePlanSelect = (planId: PlanType) => {
    setSelectedPlan(planId);
    const availableCounts = getAvailableTestCounts();
    let testCountToUse = selectedTestCount;
    if (availableCounts.length > 0 && !selectedTestCount) {
      testCountToUse = availableCounts[0];
      setSelectedTestCount(testCountToUse);
    }
    const productKey = testCountToUse ? `core_plus_${testCountToUse}_test` : null;
    const product = productKey ? products.find(p => p.key === productKey) || null : null;
    firePlanChange(planId, testCountToUse, paymentType, product);
  };

  const handleTestCountSelect = (count: number) => {
    setSelectedTestCount(count);
    const product = getSelectedProduct();
    firePlanChange(selectedPlan, count, paymentType, product);
  };

  const handleNextToDetails = () => {
    if (!selectedPlan || !selectedTestCount) return;

    const product = getSelectedProduct();
    let productId: string | null = null;

    if (product) {
      productId =
        paymentType === 'monthly'
          ? product.stripe_price_monthly_id
          : product.stripe_price_yearly_id;
    }

    const planData: SelectedPlanData = {
      type: selectedPlan,
      testCount: selectedTestCount,
      product: product,
      productId: productId,
      paymentType: paymentType
    };
    localStorage.setItem('selectedPlan', JSON.stringify(planData));

    const token = searchParams.get('token');
    const query = token ? `?token=${encodeURIComponent(token)}` : '';
    navigate(`/signup/today${query}`);
  };

  return (
    <div className='c_container subscription-page-wrapper'>
      <div className='two-colum-grid'>
        <div className='left-column'>
          <div className='become-member-text'>Become a member</div>
          <div className='journey-starts-here'>Your journey starts here</div>
          <div className='choose-between-monthly-yearly' >Choose between monthly or yearly payment 
            <span className='tooltip-icon' >
              ⓘ
              <span className='tooltip-text' >
                <span className='tooltip-text-content' >You get all services up front. You can pay in 1 annual payment or in 12 monthly payments. Monthly payments commit you to the full 12-month term.</span>
              </span>
            </span>
          </div>
          <div className='d-flex ' >
            <div className='plan-term-check' >
              <label>
                <input 
                  type='radio' 
                  name='plan-term' 
                  checked={paymentType === 'monthly'}
                  onChange={() => {
                    setPaymentType('monthly');
                    firePaymentTypeChange('monthly', selectedPlan, selectedTestCount);
                  }}
                />
                <span className='btn' >Monthly</span>
              </label>
              <label>
                <input 
                  type='radio' 
                  name='plan-term' 
                  checked={paymentType === 'yearly'}
                  onChange={() => {
                    setPaymentType('yearly');
                    firePaymentTypeChange('yearly', selectedPlan, selectedTestCount);
                  }}
                />
                <span className='btn' >Yearly</span>
              </label>
            </div>
          </div>
          <div className='' >
            {loading ? (
              <div>Loading plans...</div>
            ) : (
              <>
              
                <div
                  className={`plan-card-wrapper coreVitals-plus-membership ${selectedPlan === 'coreVitalsPlus' ? 'active' : ''}`}
                  onClick={() => handlePlanSelect('coreVitalsPlus')}
                >
                  <PlanHeader
                    id='coreVitalsPlus'
                    title='CoreVitals+ Membership'
                    description='CoreVitals+ Membership – Deeper insight across bloods combined with MRI imaging.'
                    price={getPlanPrice('coreVitalsPlus')}
                    selectedPlan={selectedPlan}
                    onSelect={(id) => handlePlanSelect(id as PlanType)}
                  />
                  {selectedPlan === 'coreVitalsPlus' && (
                    <BloodTestPerYear
                      selectedTestCount={selectedTestCount}
                      onTestCountChange={handleTestCountSelect}
                      availableTestCounts={getAvailableTestCounts()}
                    />
                  )}
                  {isMobile && selectedPlan === 'coreVitalsPlus' && (
                    <OrderSummary 
                      selectedPlanId={selectedPlan} 
                      paymentType={paymentType}
                      selectedTestCount={selectedTestCount}
                      getPlanPrice={getPlanPrice}
                    />
                  )}
                </div>
              </>
            )}

            <div>
              <button
                disabled={!selectedPlan || !selectedTestCount}
                onClick={handleNextToDetails}
                className='plans-step-btn'
              >
                Next - your details{' '}
                <img src={arrowRightIcon} alt='arrow right' />
              </button>
            </div>
          </div>
        </div>
        {!isMobile && (
          <div className='right-column'>
            <div className='inner-card'>
              <h4 className='inner-card-title'>Order summary</h4>
              {selectedPlan ? (
                <OrderSummary
                  selectedPlanId={selectedPlan}
                  paymentType={paymentType}
                  selectedTestCount={selectedTestCount}
                  getPlanPrice={getPlanPrice}
                />
              ) : (
                <div className='inner-card-info'>
                  <div className='select-plan-text'>
                    Select a plan to get started.
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlansPlus;


